import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'sonner'
import { Phone, Shield, Trash2, UserPlus, Users, UsersRound } from 'lucide-react'
import {
  createMember,
  fetchMembers,
  removeMember,
  updateMember,
  type AssignableRank,
  type Member,
} from '../../api'
import { useAuth } from '../../AuthContext'
import { RANK_BADGE_VARIANT, RANK_LABELS } from '../../lib/rank'
import { PageHeader } from '../../components/PageHeader'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Badge } from '../../components/ui/badge'
import { Skeleton } from '../../components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog'
import { Card } from '../../components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { UserAvatar } from '../../components/UserAvatar'
import { OperatorAssignmentsDialog } from '../../components/OperatorAssignmentsDialog'

const emptyForm = { nombre: '', email: '', password: '' }

export function MembersPage() {
  const { activeSociedadId, user } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [targetSociedadId, setTargetSociedadId] = useState(activeSociedadId ?? '')
  const [error, setError] = useState<string | null>(null)
  const [assignTarget, setAssignTarget] = useState<Member | null>(null)

  const activeMembership = user?.sociedades.find((s) => s.id === activeSociedadId)
  const myRank = activeMembership?.rank
  const isOwner = myRank === 'owner'
  // A qué Sociedades puede agregar miembros quien está logueado — un Owner
  // administra todas las suyas, un Administrador solo las que tiene a cargo.
  const manageableSociedades = user?.sociedades.filter((s) => s.rank !== 'operador') ?? []
  const targetSociedad = manageableSociedades.find((s) => s.id === targetSociedadId)

  const membersQuery = useQuery({
    queryKey: ['members', activeSociedadId],
    queryFn: () => fetchMembers(activeSociedadId!),
    enabled: !!activeSociedadId,
  })

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: ['members', activeSociedadId] })
  }

  const createMutation = useMutation({
    mutationFn: () => createMember(targetSociedadId, { ...form, rank: 'operador' }),
    onSuccess: (member) => {
      queryClient.invalidateQueries({ queryKey: ['members', targetSociedadId] })
      setOpen(false)
      setForm(emptyForm)
      const suffix = targetSociedadId === activeSociedadId ? '' : ` en ${targetSociedad?.nombre}`
      toast.success(`Se agregó a ${member.nombre}${suffix} — pasale el email y la contraseña para que entre.`)
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError('Ya existe una cuenta con ese email.')
      } else {
        setError('No se pudo agregar al miembro. Revisá los datos e intentá de nuevo.')
      }
    },
  })

  const rankMutation = useMutation({
    mutationFn: ({ userId, rank }: { userId: string; rank: AssignableRank }) =>
      updateMember(activeSociedadId!, userId, rank),
    onSuccess: invalidate,
    onError: () => toast.error('No se pudo cambiar el rango de ese miembro.'),
  })

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeMember(activeSociedadId!, userId),
    onSuccess: invalidate,
    onError: () => toast.error('No se pudo quitar al miembro.'),
  })

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    setError(null)
    createMutation.mutate()
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Users}
        title={activeMembership ? `Miembros de ${activeMembership.nombre}` : 'Miembros'}
        description={
          membersQuery.data
            ? `${membersQuery.data.length} ${membersQuery.data.length === 1 ? 'persona tiene' : 'personas tienen'} acceso a esta Sociedad.`
            : 'Quién puede acceder a esta Sociedad y con qué rango.'
        }
        action={
          <Dialog
            open={open}
            onOpenChange={(next) => {
              setOpen(next)
              if (next) setTargetSociedadId(activeSociedadId ?? '')
              else setError(null)
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="size-4" />
                Agregar miembro
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle>Agregar miembro</DialogTitle>
                </DialogHeader>

                {manageableSociedades.length > 1 && (
                  <div className="flex flex-col gap-1.5">
                    <Label>Sociedad</Label>
                    <Select value={targetSociedadId} onValueChange={setTargetSociedadId}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {manageableSociedades.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="member-nombre">Nombre</Label>
                  <Input
                    id="member-nombre"
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="member-email">Email</Label>
                  <Input
                    id="member-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="member-password">Contraseña</Label>
                  <Input
                    id="member-password"
                    type="text"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Mínimo 8 caracteres. Pasale este email y contraseña a la persona para que entre.
                  </p>
                </div>
                <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                  Se agregará como Operador de{' '}
                  {manageableSociedades.length > 1 ? (targetSociedad?.nombre ?? 'la Sociedad elegida') : 'esta Sociedad'}.{' '}
                  {isOwner && 'Para crear un Administrador con Sociedades a cargo, usá la sección Administradores.'}
                </p>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending || !targetSociedadId}>
                    <UserPlus className="size-4" />
                    Agregar miembro
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="overflow-hidden py-0">
        {membersQuery.isLoading && (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
            <Skeleton className="h-6 w-2/3" />
          </div>
        )}
        {membersQuery.isError && <p className="p-5 text-sm text-destructive">No se pudieron cargar los miembros.</p>}
        {membersQuery.data && membersQuery.data.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <UsersRound className="size-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Todavía no hay Administradores ni Operadores en esta Sociedad.
            </p>
          </div>
        )}
        {membersQuery.data && membersQuery.data.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rango</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {membersQuery.data.map((member: Member) => {
                const isSelf = member.user_id === user?.user_id
                // Un administrador solo gestiona operadores; el Dueño gestiona ambos.
                const canManage = isOwner || member.rank === 'operador'

                return (
                  <TableRow key={member.user_id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2.5">
                        <UserAvatar name={member.nombre} seed={member.email} size="sm" />
                        {member.nombre}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{member.email}</TableCell>
                    <TableCell>
                      <Badge variant={RANK_BADGE_VARIANT[member.rank]} className="gap-1">
                        <Shield className="size-3" />
                        {RANK_LABELS[member.rank]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {member.rank === 'operador' && (
                          <Button variant="ghost" size="sm" onClick={() => setAssignTarget(member)}>
                            <Phone className="size-3.5" />
                            Asignar números
                          </Button>
                        )}
                        {isOwner && canManage && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={rankMutation.isPending}
                            onClick={() =>
                              rankMutation.mutate({
                                userId: member.user_id,
                                rank: member.rank === 'administrador' ? 'operador' : 'administrador',
                              })
                            }
                          >
                            {member.rank === 'administrador' ? 'Hacer Operador' : 'Hacer Administrador'}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={removeMutation.isPending || isSelf || !canManage}
                          title={isSelf ? 'No podés quitarte a vos mismo' : !canManage ? 'No podés gestionar este rango' : 'Quitar de la Sociedad'}
                          onClick={() => removeMutation.mutate(member.user_id)}
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <OperatorAssignmentsDialog
        member={assignTarget}
        sociedadId={activeSociedadId}
        onClose={() => setAssignTarget(null)}
      />
    </div>
  )
}

